import {useCallback, useEffect} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {useAppRates} from '@/context/AppRatesContext';
import {AppRatesType} from '@/context/types';
import {ratesService} from '@/services/ratesService';
import type {AfriexOTCTrade} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {convertRatesListToMap} from '@/utils/rates/convertRatesListToMap';
import {showErrorMessage} from '@/utils/showErrorMessage';

export const useRatesFetcher = () => {
  const {dispatch, state} = useAppRates();
  const role = localStorage.getItem('admin-role');
  const hasOtcFetchAccess = hasAdminPermission(
    role ?? undefined,
    AfriexAdminPermission.OTCVolumeRead,
  );

  const fetchRates = useCallback(async () => {
    const initialState = {
      rawAppRates: [],
      appRates: {},
      otcVolume: [],
      loading: true,
    };
    try {
      dispatch({
        type: AppRatesType.GET_RATES,
        payload: {...initialState, loading: true},
      });

      const latestRates = await ratesService.getAppRatesList();
      let latestOTCRates: AfriexOTCTrade[] = [];
      if (hasOtcFetchAccess) {
        const returnedTradeList = await ratesService.getOTCTradeList({
          limit: 100,
        });
        latestOTCRates = returnedTradeList.data;
      }
      const rates = convertRatesListToMap(latestRates);

      dispatch({
        type: AppRatesType.GET_RATES,
        payload: {
          rawAppRates: latestRates,
          appRates: rates,
          otcVolume: latestOTCRates,
          loading: false,
        },
      });
    } catch (err) {
      dispatch({
        type: AppRatesType.GET_RATES,
        payload: initialState,
      });
      showErrorMessage(
        'Rates could not be fetched:' + getErrorMessage(err, String(err)),
      );
    }
  }, [dispatch]);

  const fetchBusinessRates = useCallback(async () => {
    const initialState = {
      businessRateList: [],
      businessRates: {},
      businessRateListLoading: false,
    };
    try {
      dispatch({
        type: AppRatesType.GET_BUSINESS_RATES,
        payload: {...initialState, businessRateListLoading: true},
      });

      const latestBusinessRates =
        await ratesService.getAppRatesList('BUSINESS');

      const rates = convertRatesListToMap(latestBusinessRates || []);

      dispatch({
        type: AppRatesType.GET_BUSINESS_RATES,
        payload: {
          businessRates: rates,
          businessRateList: latestBusinessRates,
          businessRateListLoading: false,
        },
      });
    } catch (err) {
      dispatch({
        type: AppRatesType.GET_BUSINESS_RATES,
        payload: initialState,
      });
      showErrorMessage(
        'Business rates could not be fetched:' +
          getErrorMessage(err, String(err)),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    // Fire-and-forget: both fetchers catch and surface their own failures (they
    // reset the context slice and toast), so these only stop an unexpected
    // throw becoming an unhandled rejection.
    fetchRates().catch(() => {});
    fetchBusinessRates().catch(() => {});
  }, [fetchRates, fetchBusinessRates]);

  return {
    ratesLoading: state.loading,
    appRates: state.appRates,
    otcVolume: state.otcVolume,
  };
};
